import type { EntitySet } from "building-types/entities/entitySetTypes";
import type { Group } from "building-types/groups/group";
import { isMatchExpression } from "building-types/options/matchExpressionTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { InstanceOptionSet } from "building-types/options/optionSet";
import type { DocOption, DocOptionLock, DocOptionSet } from "building-types/options/optionSet";
import type { RWTable, Table } from "common/data/table";
import { SalesCategory } from "common/types/salesCategoryTypesEnum";
import uid from "common/utils/uid";
import { formulaGetIdentifiers, isFormula } from "../formula/formulaParsing";
import { evalMatchExpression } from "../formula/matchExpression";
import { getOptionSetArchetypeOrSelf } from "./getOptionSetArchetypeOrSelf";
import { isBooleanOptionSet, optionSetDefaultOptionId, optionSetOptionIds } from "./optionSetBaseUtils";
import { validateOptionSet } from "./optionSetValidation";
import { OPTION_SET_ID_PREFIX, OPTION_ID_PREFIX } from "./optionTools";
import { isExteriorPaletteOptionSet, isInteriorPaletteOptionSet, isPaletteOptionSet } from "./paletteOptionSet";
import { isElevationOptionSet, isSystemOptionSet, isSystemOptionSetId } from "./systemOptionSet";

/**
 * Generators
 */

/**
 * Preserved for compatibility in the UI, so that a "Toggle Option" (boolean)
 * can still be easily created.
 */
const BOOLEAN_OPTION_SET_DEFAULT_NAME = "default-boolean-option-set";

export function optionSetIdNew(id?: string | number): string {
  const optionSetId = String(id ? id : uid());

  if (isSystemOptionSetId(optionSetId)) {
    return optionSetId;
  }

  if (!optionSetId.startsWith(OPTION_SET_ID_PREFIX)) {
    return OPTION_SET_ID_PREFIX + optionSetId;
  }

  return optionSetId;
}

export function optionIdNew(id?: string | number, suffix?: string): string {
  const optionId = String(id ? id : uid());

  if (optionId === "true" || optionId === "false") {
    return optionId;
  }

  if (!optionId.startsWith(OPTION_ID_PREFIX)) {
    return `${OPTION_ID_PREFIX}${optionId}${suffix ?? ""}`;
  }

  return `${optionId}${suffix ?? ""}`;
}

export function optionNewDefault(id: string, parentId: string, sortIndex: number = 0): DocOption {
  return {
    id,
    parentId,
    name: `Option ${sortIndex + 1}`,
    price: 0,
    priceTbd: true,
    disabled: false,
    description: "",
    builderOptionCode: null,
    sortIndex,
    entityType: "OPTION",
  };
}

/**
 * Boolean Option Sets maintain hard-coded IDs of true/false primarily for historical reasons.
 *
 * Many OptionChoices (home_plans.option_choices and studio_docs.option_choices) objects rely on those
 * keys to reference the Options correctly - changing this behavior would require a database migration.
 */
export function booleanOptionSetNew(name: string = BOOLEAN_OPTION_SET_DEFAULT_NAME): DocOptionSet {
  const A = "true";
  const B = "false";
  const optionSetId = optionSetIdNew();

  return {
    id: optionSetId,
    entityType: "OPTION_SET",
    defaultValue: B,
    name,
    sortIndex: 0,
    options: {
      [A]: { ...optionNewDefault(A, optionSetId, 0), name: A },
      [B]: { ...optionNewDefault(B, optionSetId, 1), name: B },
    },
    salesCategory: SalesCategory.UNCATEGORIZED,
    description: "",
    builderOptionSetCode: null,
    disabled: false,
    optionSetType: "USER",
  };
}

export function optionSetNew(name: string): DocOptionSet {
  const optionSetId = optionSetIdNew();
  const aId = optionIdNew();
  const bId = optionIdNew();

  return {
    id: optionSetId,
    entityType: "OPTION_SET",
    defaultValue: aId,
    name,
    sortIndex: 0,
    options: {
      [aId]: optionNewDefault(aId, optionSetId, 0),
      [bId]: optionNewDefault(bId, optionSetId, 1),
    },
    salesCategory: SalesCategory.UNCATEGORIZED,
    description: "",
    builderOptionSetCode: null,
    disabled: false,
    optionSetType: "USER",
  };
}

export function optionLockNew(optionSet: DocOptionSet): DocOptionLock {
  return { id: uid(), name: optionSet.name, entityType: "OPTION_LOCK", sortIndex: 0, optionSetId: optionSet.id };
}

export function isOptionLock(entity: any): entity is DocOptionLock {
  return entity?.entityType === "OPTION_LOCK";
}

/**
 * Getter functions
 */

export function optionSetName(optionSet: DocOptionSet, optionSetId: string): string {
  const name = optionSet?.name;

  const fallback = `[${[optionSetId]}]`;

  if (isExteriorPaletteOptionSet(optionSet)) {
    return name || "Exterior Palette";
  }

  if (isInteriorPaletteOptionSet(optionSet)) {
    return name || "Interior Palette";
  }

  return name || fallback;
}

/**
 * True when the set is switched off or unusable: the set-level `disabled` flag, no options table, or a
 * boolean set with either side disabled. For regular sets it ignores the individual option rows, so a set
 * whose options are all disabled still counts as not disabled here — use {@link optionSetHasNoEnabledOptions}
 * for that. For whether there's a real choice to make, see {@link optionSetHasNothingToPick}.
 */
export function optionSetIsDisabled(optionSet: DocOptionSet) {
  if (!optionSet) {
    return true;
  }

  if (optionSet.disabled) {
    return true;
  }

  if (isBooleanOptionSet(optionSet)) {
    return !!optionSet.options["true"]?.disabled || !!optionSet.options["false"]?.disabled;
  }

  return !optionSet.options;
}

export function optionSetIsReadonly(optionSet: DocOptionSet | undefined): boolean {
  return !!optionSet?.readonly;
}

export function optionSetOption(optionSet: DocOptionSet, optionId: string): DocOption | null | undefined {
  if (optionSet?.options[optionId]) {
    return optionSet.options[optionId];
  }
  return null;
}

export function optionSetOptionName(optionSet: DocOptionSet, optionId: string | undefined | null): string {
  if (optionId === undefined || optionId === null) {
    return "Unknown option";
  }
  return optionSet?.options?.[optionId]?.name ?? optionId;
}

export function optionSetBaseBuilderOptionCode(
  optionSet: DocOptionSet,
  optionSets: Table<DocOptionSet>,
): string | undefined | null {
  const defaultId = optionSetDefaultOptionId(optionSet, optionSets);
  return defaultId ? optionSet.options[defaultId]?.builderOptionCode : null;
}

export function optionSetOptionNames(optionSet: DocOptionSet | undefined): string[] {
  return Object.values(optionSet?.options ?? {}).map((x) => x.name);
}

type OptionSetSelectedOptionIdCache = Map<string, string | null>;

const optionSetSelectedOptionIdCacheRoot = new WeakMap<
  Table<DocOptionSet>,
  WeakMap<Table<InstanceOptionSet>, WeakMap<OptionChoices, OptionSetSelectedOptionIdCache>>
>();

function getOptionSetSelectedOptionIdCache(
  optionSets: Table<DocOptionSet>,
  instanceOptionSets: Table<InstanceOptionSet>,
  optionChoices: OptionChoices,
): OptionSetSelectedOptionIdCache {
  let byInstanceOptionSets = optionSetSelectedOptionIdCacheRoot.get(optionSets);
  if (!byInstanceOptionSets) {
    byInstanceOptionSets = new WeakMap();
    optionSetSelectedOptionIdCacheRoot.set(optionSets, byInstanceOptionSets);
  }

  let byOptionChoices = byInstanceOptionSets.get(instanceOptionSets);
  if (!byOptionChoices) {
    byOptionChoices = new WeakMap();
    byInstanceOptionSets.set(instanceOptionSets, byOptionChoices);
  }

  let cache = byOptionChoices.get(optionChoices);
  if (!cache) {
    cache = new Map();
    byOptionChoices.set(optionChoices, cache);
  }

  return cache;
}

function optionSetSelectedOptionIdUncached(
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
  optionSetId: string,
): string | null {
  const { optionSets } = definitions;
  if (!optionSets) {
    return null;
  }

  const optionSet = getOptionSetArchetypeOrSelf(definitions, optionSetId);

  if (!optionSet) {
    return null;
  }

  // for all option set types, return `optionChoices[optionSetId]` if explicitly set and valid
  if (optionChoices) {
    const value = optionChoices[optionSetId];
    if (validateOptionSet(optionSet, value) === null) {
      return value;
    }
  }

  // No explicit choice — archetype and instance ids both resolve to defaultValue (incl. template overrides).
  return optionSetDefaultOptionId(optionSet, optionSets);
}

/**
 * Resolves the effective option choice for an option set, looking at `optionChoices` and falling
 * back to `defaultValue`. Instance ids without an explicit entry resolve to the archetype's
 * default only — not the archetype's stored choice. The `optionChoices` argument may contain
 * transient values produced during option resolution / building generation — not just the
 * persisted user-choice record.
 */
export function optionSetSelectedOptionId(
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
  optionSetId: string,
): string | null {
  const { optionSets, instanceOptionSets } = definitions;
  if (!optionSets) {
    return null;
  }

  const cache = getOptionSetSelectedOptionIdCache(optionSets, instanceOptionSets, optionChoices);
  const cached = cache.get(optionSetId);
  if (cached !== undefined) {
    return cached;
  }

  const result = optionSetSelectedOptionIdUncached(definitions, optionChoices, optionSetId);
  cache.set(optionSetId, result);
  return result;
}

export function optionSetsDefaultOptions(optionSets: Table<DocOptionSet>) {
  const vals = {};

  for (const key in optionSets) {
    vals[key] = optionSetDefaultOptionId(optionSets[key], optionSets);
  }

  return vals;
}

export function showroomOptionName(option: Pick<DocOption, "name" | "id">, optionSet: DocOptionSet): string {
  if (isElevationOptionSet(optionSet.id)) {
    return "Elevation: " + (option.name ?? option.id);
  }
  return optionName(option, optionSet);
}

export function optionName(option: Pick<DocOption, "name" | "id">, optionSet: DocOptionSet): string {
  if (isBooleanOptionSet(optionSet)) {
    return optionSet.name;
  }
  return option.name ?? option.id;
}

export function optionPrice(option: Pick<DocOption, "price"> | null | undefined, choices?: OptionChoices): number {
  if (option) {
    if (isMatchExpression(option.price)) {
      if (choices) {
        const { value } = evalMatchExpression(option.price, (id) => choices[id]);
        return value as number;
      }
      return option.price.defaultValue as number;
    }
    if (typeof option.price === "number") {
      return option.price;
    }
  }
  return 0;
}

/**
 * Option sets that have an option whose price is a formula (MatchExpression) referencing a
 * *different* set. Such a price can change when the option-rule cascade alters that other set, so a
 * caller that shortcuts the cascade must still run it for these sets. Self-only formulas are excluded
 * — forcing the set already fixes its own value.
 */
export function getFormulaPriceDependentOptionSetIds(optionSets: Table<DocOptionSet>): Set<string> {
  const ids = new Set<string>();
  for (const optionSet of Object.values(optionSets)) {
    for (const option of Object.values(optionSet.options)) {
      const price = option.price;
      if (!isMatchExpression(price)) {
        continue;
      }
      const referencesOtherSet = price.cases.some((matchCase) =>
        // formulaGetIdentifiers returns the referenced option-set ids and is null-safe (returns []
        // for a missing condition), guarding against legacy/hand-authored data.
        formulaGetIdentifiers(matchCase.condition).some((setId) => setId !== optionSet.id),
      );
      if (referencesOtherSet) {
        ids.add(optionSet.id);
        break;
      }
    }
  }
  return ids;
}

export function formatOptionPrice(
  option: Pick<DocOption, "price" | "priceTbd"> | null | undefined,
  choices?: OptionChoices,
): string | undefined {
  if (!option?.price) {
    return undefined;
  }

  if (option.priceTbd) {
    return "TBD";
  }

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  const price = optionPrice({ price: option.price }, choices);
  return formatter.format(price);
}

/**
 * Misc. utilities
 */

export function optionSetDisabledOptionIds(optionSet: DocOptionSet): string[] {
  const ids = optionSetOptionIds(optionSet);

  if (!optionSet?.options) {
    return [];
  }

  const disabledOptionIds: string[] = [];

  for (const id of ids) {
    if (optionSet.options[id] && optionSet.options[id].disabled) {
      disabledOptionIds.push(id);
    }
  }

  return disabledOptionIds;
}

export function optionSetEnabledOptionIds(optionSet: DocOptionSet): string[] {
  const ids = optionSetOptionIds(optionSet);

  if (!optionSet || !optionSet.options) {
    return ids;
  }

  const enabledOptionIds: string[] = [];

  for (const id of ids) {
    if (!optionSet.options[id] || !optionSet.options[id].disabled) {
      enabledOptionIds.push(id);
    }
  }

  return enabledOptionIds;
}
/**
 * Search a DocOptionSet across the following:
 *
 * 1. Internal Higharc ID (e.g. "elevationStyle" or "mirror")
 * 2. Option display name
 * 3. Builder option ID of any values
 * 4. Display name of any values
 * 5. Category
 * 6. Any `extraSearchableValues` supplied by the caller (e.g. a component instance name)
 */

export function searchOptionSet(
  optionSetId: string,
  optionSet: DocOptionSet,
  query: string,
  extraSearchableValues?: readonly (string | undefined)[],
): boolean {
  const category = optionSet.category;
  const name = optionSet.name;

  let optionNames = "";
  let optionIds = "";

  for (const valueId in optionSet?.options) {
    optionNames += optionSet?.options[valueId].name + " ";
    optionIds += optionSet?.options[valueId].builderOptionCode + " ";
  }

  return (
    !query ||
    [optionSetId, category, name, optionNames, optionIds, ...(extraSearchableValues ?? [])]
      .filter((x) => !!x)
      .findIndex((x) => (x?.toLowerCase().indexOf(query.toLowerCase()) ?? -1) >= 0) >= 0
  );
}

export function canAddOptionToSet(optionSet: DocOptionSet): boolean {
  return !isBooleanOptionSet(optionSet) && !isSystemOptionSet({ type: "OPTION_SET", id: optionSet.id });
}

export function areOptionSetIdsEqual(optionSetIdA: string, optionSetIdB: string): boolean {
  // normalize ids to support old and new id formats
  return optionSetIdNew(optionSetIdA) === optionSetIdNew(optionSetIdB);
}

export function getOptionSetId(selectedPast: readonly EntitySet[], group: Group): string | null {
  if (isFormula(group.disabled)) {
    const entitySet: EntitySet = selectedPast?.[selectedPast?.length - 1]; // last entity selected in selection history
    if (entitySet && Object.values(entitySet).length === 1) {
      const entity = Object.values(entitySet)[0];
      if (
        /* check optionSetId is being used in group conditions predicate */
        entity.type === "OPTION_SET" &&
        formulaGetIdentifiers(group.disabled)?.findIndex((x) => x === entity.id) !== -1
      ) {
        return entity.id;
      }
    }
  }
  return null;
}

/**
 * True when an option set has nothing enabled at all: no options, or every option disabled. Such
 * a set can never contribute anything on any path, so it is dropped before option state is built.
 */
export function optionSetHasNoEnabledOptions(optionSet: DocOptionSet): boolean {
  if (!optionSet?.options) {
    return true;
  }

  return Object.values(optionSet.options).every((option) => option.disabled);
}

/**
 * True when the set gives the buyer no real choice: no enabled options, or one non-palette option (a forced value).
 * Ignores `optionSet.disabled`; see {@link optionSetIsDisabled}.
 * Callers skip these when generating state and applying choice mutations
 */
export function optionSetRecordsNoDecision(optionSet: DocOptionSet): boolean {
  if (optionSetHasNoEnabledOptions(optionSet)) {
    return true;
  }

  // Palettes are exempt until SHO-535 -- for now keep them even with a single enabled swatch.
  if (isPaletteOptionSet(optionSet)) {
    return false;
  }

  return optionSetEnabledOptionIds(optionSet).length < 2;
}

/** True when CompactOptionSetInput should be inert (set off, or fewer than two enabled options). */
export function optionSetHasNothingToPick(optionSet: DocOptionSet): boolean {
  if (!optionSet?.options) {
    return true;
  }

  return !!optionSet.disabled || optionSetEnabledOptionIds(optionSet).length < 2;
}

export function getOptionImages(option: (Pick<DocOption, "salesImages"> & { thumbnailUrl?: string }) | undefined) {
  const images = option?.salesImages ? [...option.salesImages] : [];

  // For elevations, which are typed and handled "weirdly", that may have a thumbnailUrl:
  const thumbnailUrl = option?.thumbnailUrl;
  if (thumbnailUrl && images.every((img) => img.url !== thumbnailUrl)) {
    images.unshift({ url: thumbnailUrl, title: thumbnailUrl.split("/").pop() });
  }

  return images;
}

export function formatOptionSetListString(
  optionSets: DocOptionSet[],
  options: Intl.ListFormatOptions = { style: "long", type: "conjunction" },
): string {
  const list = Object.values(optionSets);
  const formatter = new Intl.ListFormat("en-US", options);
  return formatter.format(list.map((x) => x.name));
}

export function collectDisabledOptions(optionSets: Table<DocOptionSet>): Table<DocOption> {
  const disabledOptions: RWTable<DocOption> = {};

  for (const optionSetId in optionSets) {
    const optionSet = optionSets[optionSetId];

    // Due to boolean option set options having hard-coded IDs of true/false, we omit their collection
    if (isBooleanOptionSet(optionSet)) {
      continue;
    }

    for (const optionId in optionSet.options) {
      const option = optionSet.options[optionId];

      if (option.disabled) {
        disabledOptions[optionId] = option;
      }
    }
  }

  return disabledOptions;
}
